import { create } from 'zustand';
import * as api from '../lib/api';
import type { ChannelMember } from '../lib/api';

interface ChannelMemberState {
  byChannelId: Record<string, ChannelMember[]>;
  loadedVersionByChannelId: Record<string, number>;
  loadChannelMembers: (channelId: string, version?: number) => Promise<ChannelMember[]>;
  updateChannelMember: (channelId: string, userId: string, updates: Partial<ChannelMember>) => void;
  clearChannel: (channelId: string) => void;
  reset: () => void;
}

const inFlight = new Map<string, Promise<ChannelMember[]>>();
let membersGeneration = 0;

export const useChannelMemberStore = create<ChannelMemberState>()((set, get) => ({
  byChannelId: {},
  loadedVersionByChannelId: {},

  loadChannelMembers: (channelId, version = 0) => {
    const generation = membersGeneration;
    const state = get();
    const cached = state.byChannelId[channelId];
    const loadedVersion = state.loadedVersionByChannelId[channelId];
    if (cached && loadedVersion !== undefined && loadedVersion >= version) {
      return Promise.resolve(cached);
    }

    const key = `${channelId}:${version}`;
    const pending = inFlight.get(key);
    if (pending) return pending;

    const request = api.fetchChannelMembers(channelId).then((members) => {
      if (generation !== membersGeneration) return [];
      set((current) => {
        const currentVersion = current.loadedVersionByChannelId[channelId] ?? -1;
        if (currentVersion > version) return current;
        return {
          byChannelId: { ...current.byChannelId, [channelId]: members },
          loadedVersionByChannelId: {
            ...current.loadedVersionByChannelId,
            [channelId]: version,
          },
        };
      });
      return members;
    }).finally(() => {
      inFlight.delete(key);
    });
    inFlight.set(key, request);
    return request;
  },

  updateChannelMember: (channelId, userId, updates) => set((state) => {
    const members = state.byChannelId[channelId];
    if (!members?.some(member => member.user_id === userId)) return state;
    return {
      byChannelId: {
        ...state.byChannelId,
        [channelId]: members.map(member => member.user_id === userId
          ? { ...member, ...updates }
          : member),
      },
    };
  }),

  clearChannel: (channelId) => set((state) => {
    const byChannelId = { ...state.byChannelId };
    const loadedVersionByChannelId = { ...state.loadedVersionByChannelId };
    delete byChannelId[channelId];
    delete loadedVersionByChannelId[channelId];
    return { byChannelId, loadedVersionByChannelId };
  }),

  reset: () => {
    membersGeneration++;
    inFlight.clear();
    set({ byChannelId: {}, loadedVersionByChannelId: {} });
  },
}));
